import { giphyBlue, giphyGreen, giphyPink, giphyPurple } from '@giphy/colors'
import { ElementType } from 'react'
import { EraseIcon, LassoIcon, MagicWandIcon, PaintIcon } from '../components/icons'

/**
 * Cut out mode for removing background.
 */
enum CreationToolCutOutMode {
    Lasso = 'CT_CUT_OUT_MODE_LASSO',
    Paint = 'CT_CUT_OUT_MODE_PAINT',
    MagicWand = 'CT_CUT_OUT_MODE_MAGIC_WAND',
    Erase = 'CT_CUT_OUT_MODE_ERASE',
}

namespace CreationToolCutOutMode {
    /**
     * Returns all of the modes in order.
     */
    export function all(): CreationToolCutOutMode[] {
        return [
            CreationToolCutOutMode.Lasso,
            CreationToolCutOutMode.Paint,
            CreationToolCutOutMode.MagicWand,
            CreationToolCutOutMode.Erase,
        ]
    }

    /**
     * Returns the mode's icon/active color.
     */
    export function color(tool: CreationToolCutOutMode): string {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return giphyPurple
            case CreationToolCutOutMode.Paint:
                return giphyGreen
            case CreationToolCutOutMode.MagicWand:
                return giphyBlue
            case CreationToolCutOutMode.Erase:
                return giphyPink
        }
    }

    /**
     * Returns the mode's title.
     */
    export function title(tool: CreationToolCutOutMode): string {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return 'Lasso'
            case CreationToolCutOutMode.Paint:
                return 'Brush'
            case CreationToolCutOutMode.MagicWand:
                return 'Magic Wand'
            case CreationToolCutOutMode.Erase:
                return 'Eraser'
        }
    }

    /**
     * Returns the mode's instruction text.
     */
    export function instructions(tool: CreationToolCutOutMode): string {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return 'Click around to make an outline of your sticker.'
            case CreationToolCutOutMode.Paint:
                return 'Click & drag to paint areas back into your sticker'
            case CreationToolCutOutMode.MagicWand:
                return 'Click to remove the background magically'
            case CreationToolCutOutMode.Erase:
                return 'Click & drag to erase pieces of your sticker'
        }
    }

    /**
     * Returns the tool's instruction image/gif url.
     */
    export function instructionsImageUrl(tool: CreationToolCutOutMode): string {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return '/static/img/creation_tools/cut-out/lasso-instructions.gif'
            case CreationToolCutOutMode.Erase:
                return '/static/img/creation_tools/cut-out/eraser-instructions.gif'
            case CreationToolCutOutMode.Paint:
                return '/static/img/creation_tools/cut-out/brush-instructions.gif'
            case CreationToolCutOutMode.MagicWand:
                return '/static/img/creation_tools/cut-out/magic-instructions.gif'
            default:
                return ''
        }
    }

    /**
     * Returns the mode's icon component.
     */
    export function icon(tool: CreationToolCutOutMode): ElementType {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return LassoIcon
            case CreationToolCutOutMode.Paint:
                return PaintIcon
            case CreationToolCutOutMode.MagicWand:
                return MagicWandIcon
            case CreationToolCutOutMode.Erase:
                return EraseIcon
        }
    }

    /**
     * Returns the mode's icon sprite animation url.
     */
    export function iconAnimationUrl(tool: CreationToolCutOutMode): string {
        switch (tool) {
            case CreationToolCutOutMode.Lasso:
                return '/static/img/creation_tools/cut-out/lasso.png'
            case CreationToolCutOutMode.Paint:
                return '/static/img/creation_tools/cut-out/paint.png'
            case CreationToolCutOutMode.MagicWand:
                return '/static/img/creation_tools/cut-out/magic.png'
            case CreationToolCutOutMode.Erase:
                return '/static/img/creation_tools/cut-out/erase.png'
        }
    }
}

export default CreationToolCutOutMode
